import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { ArrowDownLeft, ArrowUpRight, Building2, Stethoscope, ListTodo, Info } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { TopicSelect } from "./TopicSelect";
import { MailActions } from "./MailActions";

export const fmtMailDate = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const METHOD = { email: "per Adresse", domain: "per Domain", rule: "per Regel", manual: "manuell" };
export const targetUrl = (e) => (e.target_type === "vendor" ? `/medical-software/${e.target_id}` : `/customers/${e.target_id}`);
const people = (list) => (list || []).map((r) => r.name || r.email).join(", ");

export function WorkStateBadge({ e }) {
  if (!e.work_state) return null;
  const done = e.work_state === "done";
  return (
    <span className="inline-flex items-center gap-1.5 text-xs" data-testid={`mail-work-state-${e.id}`}>
      <Badge variant="outline" className={done ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-sky-200 bg-sky-50 text-sky-700"}>{done ? "Erledigt" : "In Bearbeitung"}</Badge>
      {e.task_id && <Link to="/tasks" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`mail-task-link-${e.id}`}><ListTodo className="w-3 h-3" />Aufgabe{e.task_assignee ? ` bei ${e.task_assignee}` : ""}</Link>}
    </span>
  );
}

export function TargetLink({ e }) {
  if (e.status !== "assigned" || !e.target_id) return null;
  const Icon = e.target_type === "vendor" ? Stethoscope : Building2;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <Link to={targetUrl(e)} className="inline-flex items-center gap-1 font-medium text-[#1E3A5F] hover:underline" data-testid={`mail-target-${e.id}`}><Icon className="w-3.5 h-3.5" />{e.target_name}</Link>
      <span className="text-muted-foreground">· {METHOD[e.match_method] || "zugeordnet"}</span>
    </span>
  );
}

export function MailDetail({ email: e, onChanged }) {
  const { user } = useAuth();
  const sent = e.folder === "sent";
  return (
    <article className="flex flex-col h-full min-h-0" data-testid={`mail-detail-${e.id}`}>
      <header className="p-5 border-b space-y-3">
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${sent ? "bg-sky-50 text-sky-600" : "bg-[#FFF5EC] text-[#E8700A]"}`} title={sent ? "Gesendet" : "Eingang"}>
            {sent ? <ArrowUpRight className="w-5 h-5" /> : <ArrowDownLeft className="w-5 h-5" />}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold leading-snug break-words" data-testid="mail-detail-subject">{e.subject}</h2>
            <p className="text-sm text-muted-foreground mt-0.5 break-words">
              {sent ? <>An <span className="text-foreground">{people(e.to)}</span></> : <>Von <span className="text-foreground font-medium">{e.from_name || e.from_email}</span>{e.from_name ? ` <${e.from_email}>` : ""}</>}
            </p>
            {!sent && e.to?.length > 0 && <p className="text-xs text-muted-foreground break-words">An {people(e.to)}{e.cc?.length ? ` · Cc ${people(e.cc)}` : ""}</p>}
            <p className="text-xs text-muted-foreground mt-1">{fmtMailDate(e.received_at)}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <TopicSelect email={e} canEdit={user?.role !== "readonly"} onChanged={onChanged} />
          <TargetLink e={e} />
          <WorkStateBadge e={e} />
          {e.status === "ignored" && <Badge variant="outline">Ignoriert</Badge>}
        </div>
        <div className="pt-3 border-t"><MailActions email={e} onChanged={onChanged} /></div>
      </header>
      <div className="p-6 flex-1 overflow-y-auto space-y-4">
        <p className="text-sm leading-relaxed whitespace-pre-line text-slate-800" data-testid="mail-detail-body">{e.excerpt || "Kein Textauszug vorhanden."}</p>
        <p className="flex items-start gap-2 text-xs text-muted-foreground rounded-lg bg-muted/60 px-3 py-2">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />Im CRM wird nur ein Textauszug gespeichert. {e.web_link ? "Die vollständige Mail inkl. Anhängen öffnen Sie über „Outlook“." : ""}
        </p>
      </div>
    </article>
  );
}
